import { FC } from 'react';
import { Box, Button } from '@mui/material';
import { StyledLink, MenuBox } from '../style';
import whiteLogo from '../../../../media/whiteLogo.png';
import darkLogo from '../../../../media/blackLogo.png';
import '../logoStyles.css';
import MobileMenu from '../mobileMenu/MobileMenu';
import { useAppDispatch } from '../../../../hooks';
import useTranslations from '../../../global/translation/hooks/translation';
import { scrollFn } from '../helper';
import { useThemeContext } from '../../../global/ThemeContext';
import { setLanguage } from '../../../global/translation/translationSlice';
import DarkModeToggle from '../../DarkModeToggle';
import { designTokens } from '../../../../theme/designTokens';

const SecondAppBar: FC = () => {
  const { currentLanguage, translations } = useTranslations();
  const { darkMode } = useThemeContext();
  const dispatch = useAppDispatch();
  const palette = darkMode ? designTokens.colors.dark : designTokens.colors.light;
  const navColor = palette.text.primary;

  // Place this component above SecondAppBar or in a separate file
  const MenuNavItem: FC<{ onClick: () => void; label: string; featured?: boolean }> = ({ onClick, label, featured = false }) => (
    <Box
      sx={{
        cursor: 'pointer',
        mx: { lg: 0.35, xl: 0.7 },
        color: featured ? palette.primary : navColor,
        fontWeight: featured ? 800 : 400,
        fontSize: { lg: 13, xl: 15 },
        display: 'block',
        position: 'relative',
        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        fontFamily: 'inherit',
        px: featured ? { lg: 1, xl: 1.25 } : { lg: 0.5, xl: 0.8 },
        py: featured ? 0.7 : 1,
        border: featured ? `1px solid ${palette.border.emphasis}` : '1px solid transparent',
        borderRadius: featured ? designTokens.radius.md : 0,
        bgcolor: featured ? palette.background.subtle : 'transparent',
        whiteSpace: 'nowrap',
        '&::after': {
          content: '""',
          display: featured ? 'none' : 'block',
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: 0,
          height: '2px',
          backgroundColor: palette.accent,
          transition: 'width 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        },
        '&:hover': {
          bgcolor: featured ? palette.background.elevated : 'transparent',
          transform: featured ? 'translateY(-1px)' : 'none',
          '&::after': {
            width: '100%',
          },
        },
      }}
      onClick={onClick}
    >
      {label}
    </Box>
  );

  return (
    <>
      <Box
        sx={{
          px: { xs: 2, lg: 4, xl: 10 },
          background: darkMode ? 'rgba(7, 20, 43, 0.96)' : 'rgba(255, 255, 255, 0.96)',
          backdropFilter: 'blur(8px)',
          color: navColor,
          borderBottom: `1px solid ${darkMode ? 'rgba(141,178,255,0.24)' : 'rgba(20,56,125,0.10)'}`,
          boxShadow: darkMode ? 'none' : '0 8px 24px rgba(20, 56, 125, 0.06)',
        }}
      >
        <MobileMenu />
        <MenuBox
          sx={{
            display: { xs: 'none', lg: 'flex' },
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          {/* Logo on the left */}
          <Box sx={{ flex: '0 0 auto', pr: { lg: 1, xl: 2 } }}>
            <StyledLink to={'/'} onClick={() => scrollFn('top')}>
              <img
                style={{
                  scale: '0.7',
                }}
                alt="urban_equestrian"
                src={darkMode ? whiteLogo : darkLogo}
                className="whiteLogoStyles"
              />
            </StyledLink>
          </Box>
          {/* Menu items in the center */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { lg: 0.25, xl: 1.25 }, flex: 1, justifyContent: 'center' }}>
            {[
              { key: 'aboutUs', section: 'about', icon: <span style={{ marginRight: 4 }}></span>, featured: false },
              { key: 'abeo', section: 'abeo', icon: <span style={{ marginRight: 4 }}></span>, featured: true },
              { key: 'offer', section: 'offer', icon: <span style={{ marginRight: 4 }}></span>, featured: false },
              { key: 'horses', section: 'horses', icon: <span style={{ marginRight: 4 }}></span>, featured: false },
              { key: 'method', section: 'method', icon: <span style={{ marginRight: 4 }}></span>, featured: false },
              { key: 'pricing', section: 'pricing', icon: <span style={{ marginRight: 4 }}></span>, featured: false },
              { key: 'ourTeam', section: 'team', icon: <span style={{ marginRight: 4 }}></span>, featured: false },
              { key: 'contact', section: 'contact', icon: <span style={{ marginRight: 4 }}></span>, featured: false },
            ].map(({ key, section, icon, featured }) => (
              <Box key={key} sx={{ display: 'flex', alignItems: 'center' }}>
                {icon}
                <MenuNavItem onClick={() => scrollFn(section)} label={translations.menuItems[key][currentLanguage]} featured={featured} />
              </Box>
            ))}
          </Box>
          {/* Theme and language controls on the right */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { lg: 0.75, xl: 1.5 }, flex: '0 0 auto' }}>
            <DarkModeToggle />
            <Button
              onClick={() => {
                currentLanguage === 'en' ? dispatch(setLanguage('pl')) : dispatch(setLanguage('en'));
              }}
              size="medium"
              sx={{ minWidth: 0, px: { lg: 1, xl: 1.5 }, fontWeight: 700, color: navColor }}
            >
              {currentLanguage === 'en' ? 'PL' : 'EN'}
            </Button>
          </Box>
        </MenuBox>
      </Box>
    </>
  );
};

export default SecondAppBar;
